import { useCallback, useMemo } from 'react';
import { ListItemField } from '@common-fe/common-fe';

import Permissions from '@/common/permissions';
import ROUTES from '@/common/routes';
import { useHasAccess, useHistory } from '@/modules/core/hooks';
import { EmployerStatus } from '@/modules/employer/employer.constants';
import { Employer } from '@/modules/employer/employer.types';
import { getIdsFromPath } from '@/utils';

const useConfigureOption = (list: Employer[]) => {
  const canEditOrganization = useHasAccess([{ permission: Permissions.EDIT_ER_DATA }]);
  const { push } = useHistory();

  const handleRedirectToOrgSetup = useCallback((org: Employer) => {
    const { partnerId, distributorId, employerId, subsidiaryId } = getIdsFromPath(org?.path);
    
    if (subsidiaryId) {
      return push(ROUTES.SUBSIDIARY_VIEW(org?.id));
    }
    if (employerId) {
      if (org?.status?.toUpperCase() === EmployerStatus.ExpressSetup || org?.status?.toUpperCase() === EmployerStatus.Abandoned) {
        return push(ROUTES.EXPRESS_SETUP_VIEW(org?.id));
        
      } else {
        return push(`${ROUTES.EMPLOYERS}/${org?.id}`);
      }
    }
    if (distributorId) {
      return push(ROUTES.DISTRIBUTORS_VIEW(org?.id));
    }
    if (partnerId) {
      return push(`${ROUTES.PARTNERS}/${org?.id}`);
    }
  }, [push]);

  const handleConfigure = useCallback((id?: string) => {
    const item = list?.find((item) => item?.id === id);
    if (item) handleRedirectToOrgSetup(item);
  }, [handleRedirectToOrgSetup, list]);

  const configureOption = useMemo(() => ({
    name: 'Configure',
    onClick: handleConfigure,
    isAvailable: (_fields: ListItemField[], id: string) =>
      list.find((item) => item?.id === id)?.status?.toUpperCase() !== EmployerStatus.Terminated,
  }), [handleConfigure, list]);

  const viewProfileOption = useMemo(() => ({
    name: 'View Profile',
    onClick: handleConfigure,
    isAvailable: (_fields: ListItemField[], id: string) =>
      list.find((item) => item?.id === id)?.status?.toUpperCase() === EmployerStatus.Terminated,
  }), [handleConfigure, list]);

  if (canEditOrganization) return [configureOption, viewProfileOption];

  return [viewProfileOption];
};

export default useConfigureOption;
